import type { ReactNode } from "react";
import { Link } from "react-router";
import { freeShippingEnabled, freeShippingMin, freeShippingZones, pickupArea, pickupEnabled, zonesText } from "../../shared/shipping";
import { formatArs } from "../../shared/money";
import { useI18n } from "../i18n/I18nProvider";
import { useCatalog } from "../lib/CatalogProvider";
import { useMoney } from "../lib/CurrencyProvider";

const icon = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const TRUCK = icon(
  <>
    <path d="M2 6h11v10H2zM13 9h4.5L21 12.5V16h-8" />
    <circle cx="6.5" cy="17.5" r="2" />
    <circle cx="17" cy="17.5" r="2" />
  </>,
);
const BANK = icon(<path d="M3 9.5 12 4l9 5.5M5 10v7M9.7 10v7M14.3 10v7M19 10v7M3 20h18" />);
const PIN = icon(
  <>
    <path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11Z" />
    <circle cx="12" cy="10" r="2.3" />
  </>,
);
const SHIELD = icon(
  <>
    <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.3 7.5 9.5 4.4-1.2 7.5-4.9 7.5-9.5V6Z" />
    <path d="m8.7 12 2.3 2.3 4.4-4.6" />
  </>,
);

interface Perk {
  key: string;
  icon: ReactNode;
  title: string;
  sub: string;
  to: string;
}

/** The thin strip under the home banner: shipping, transfer price, pickup, stock. Texts follow the shop settings. */
export function PerksStrip() {
  const { t, pick, locale } = useI18n();
  const { settings } = useCatalog();
  const { transferPrices } = useMoney();
  const value = (k: string) => (settings[k] ? pick(settings[k]) : null);

  const perks: Perk[] = [];
  const zones = freeShippingZones(value("free_shipping_zones"));
  if (freeShippingEnabled(value("free_shipping_enabled")) && zones.length)
    perks.push({
      key: "shipping",
      icon: TRUCK,
      title: t("perks.freeShipping", { zones: zonesText(zones, locale) }),
      sub: t("perks.freeShippingMin", { min: formatArs(freeShippingMin(value("free_shipping_min_ars")), locale) }),
      to: "/envios",
    });
  perks.push(
    transferPrices
      ? { key: "transfer", icon: BANK, title: t("perks.transfer"), sub: t("perks.transferSub"), to: "/envios" }
      : { key: "transfer", icon: BANK, title: t("perks.payments"), sub: t("perks.paymentsSub"), to: "/envios" },
  );
  if (pickupEnabled(value("pickup_enabled")))
    perks.push({ key: "pickup", icon: PIN, title: t("perks.pickup", { area: pickupArea(value("pickup_area")) }), sub: t("perks.pickupSub"), to: "/envios" });
  perks.push({ key: "stock", icon: SHIELD, title: t("perks.stock"), sub: t("perks.stockSub"), to: "/guia-de-compra" });

  return (
    <section className="perks" aria-label={t("perks.label")}>
      <ul className="container perks-list">
        {perks.map((p) => (
          <li key={p.key}>
            <Link to={p.to} className="perk">
              <span className="perk-icon">{p.icon}</span>
              <span className="perk-text">
                <strong>{p.title}</strong>
                <span>{p.sub}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
